import React, { useState } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  FormControlLabel,
  Switch
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ChromePicker } from "react-color";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1, 0),
    width: "100%"
  },
  colorPicker: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  colorPreview: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    margin: theme.spacing(1, 0),
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    cursor: "pointer"
  },
  label: {
    display: "block",
    marginBottom: theme.spacing(1),
    fontWeight: 500
  }
}));

const NewStageDialog = ({ open, onClose, queueId, existingStages }) => {
  const classes = useStyles();
  
  const [name, setName] = useState("");
  const [color, setColor] = useState("#2196f3");
  const [isInitialStage, setIsInitialStage] = useState(false);
  const [isFinalStage, setIsFinalStage] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasInitialStage = existingStages?.some(stage => stage.isInitialStage);
  const hasFinalStage = existingStages?.some(stage => stage.isFinalStage);

  const handleColorChange = (color) => {
    setColor(color.hex);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleSave = async () => {
    if (!name) {
      toast.error(i18n.t("Stage name is required"));
      return;
    }
    
    try {
      setLoading(true);
      
      const stageData = {
        name,
        color,
        isInitialStage,
        isFinalStage,
        queueId: parseInt(queueId),
        position: existingStages ? existingStages.length : 0
      };
      
      await api.post("/kanban/stages", stageData);
      
      toast.success(i18n.t("kanban.notifications.stageCreated"));
      
      // Limpar formulário
      setName("");
      setColor("#2196f3");
      setIsInitialStage(false);
      setIsFinalStage(false);
      setShowColorPicker(false);
      
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.stageSaveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Limpar formulário
    setName("");
    setColor("#2196f3");
    setIsInitialStage(false);
    setIsFinalStage(false);
    setShowColorPicker(false);
    
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="new-stage-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="new-stage-dialog-title">
        {i18n.t("kanban.settings.newStage")}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label={i18n.t("kanban.settings.stageName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          margin="dense"
          variant="outlined"
          className={classes.formControl}
        />
        
        <div>
          <label className={classes.label}>
            {i18n.t("kanban.settings.stageColor")}
          </label>
          <div 
            className={classes.colorPreview}
            style={{ backgroundColor: color }}
            onClick={toggleColorPicker}
          />
          {showColorPicker && (
            <div className={classes.colorPicker}>
              <ChromePicker 
                color={color}
                onChange={handleColorChange}
                disableAlpha
              />
            </div>
          )}
        </div>
        
        <FormControlLabel
          control={
            <Switch
              checked={isInitialStage}
              onChange={(e) => setIsInitialStage(e.target.checked)}
              color="primary"
              disabled={hasInitialStage}
            />
          }
          label={i18n.t("kanban.settings.isInitialStage")}
          className={classes.formControl}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={isFinalStage}
              onChange={(e) => setIsFinalStage(e.target.checked)}
              color="primary"
              disabled={hasFinalStage}
            />
          }
          label={i18n.t("kanban.settings.isFinalStage")}
          className={classes.formControl}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="default">
          {i18n.t("kanban.buttons.cancel")}
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary"
          disabled={loading || !name}
        >
          {i18n.t("kanban.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewStageDialog;